<template>
  <div>
    <div class="flex gap-2 align-items-end justify-content-between w-5">
      <div class="flex gap-2 justify-content-between align-items-end w-full">
        <div class="flex flex-column gap-2 w-6">
          <p class="font-medium">Filtrar actuaciones</p>
          <Dropdown
            v-model="selectedField"
            :options="filtersFields"
            optionLabel="name"
            key="name"
            placeholder="Seleccionar tipo de filtro"
            style="border-radius: 6px"
            class="custom-focus flex-grow-1"
            :disabled="isLoading"
          />
        </div>
        <div class="w-6">
          <template v-if="inputType === 'dropdown'">
            <Dropdown
              v-model="selectedValue"
              :options="getDropdownOptions(selectedField?.field)?.options || []"
              :optionLabel="getDropdownOptions(selectedField?.field)?.optionLabel || 'name'"
              :optionValue="getDropdownOptions(selectedField?.field)?.optionValue || 'value'"
              placeholder="Seleccione una opción"
              style="border-radius: 6px"
              class="custom-focus w-full"
              :disabled="isLoading"
            />
          </template>

          <template v-else-if="inputType === 'text'">
            <InputText
              v-model="selectedValue"
              placeholder="Ingrese texto"
              style="border-radius: 6px"
              class="custom-focus w-full"
            />
          </template>

          <template v-else-if="inputType === 'date'">
            <div class="flex justify-content-center align-items-end gap-2">
              <Calendar
                placeholder="Desde"
                dateFormat="dd-mm-yy"
                class="w-12rem custom-focus"
                style="border-radius: 6px"
                v-model="filtros.FechaCreacionDesde"
                :maxDate="hoy"
              />
              <p>-</p>
              <Calendar
                v-model="filtros.FechaCreacionHasta"
                placeholder="Hasta"
                dateFormat="dd-mm-yy"
                class="w-12rem custom-focus"
                style="border-radius: 6px !important"
                :maxDate="hoy"
              />
            </div>
          </template>
        </div>
      </div>
      <Button class="btn btn-primary flex-grow-0" @click="handleFilter" :disabled="isLoading">
        <div>
          <Search color="white" :size="20" />
        </div>
        Buscar
      </Button>
      <Button
        class="btn btn-outline-primary flex-grow-0"
        @click="cleanFilters"
        :disabled="isLoading"
      >
        <div>
          <BrushCleaning color="#0B5195" :size="20" />
        </div>
        Limpiar
      </Button>
    </div>
    <div class="flex flex-wrap gap-2 mt-3">
      <div
        v-for="(filtro, key) in activeFilters"
        v-if="filtro.visible !== false"
        :key="key"
        class="p-chip flex align-items-center justify-content-between"
        style="background-color: #eff6fb; color: #0b5195; padding: 6px 12px; border-radius: 9999px"
      >
        {{ filtro.label }}: {{ formatDisplayValue(filtro.value, filtro.field) }}
        <CircleX
          :size="20"
          color="red"
          class="ml-2 cursor-pointer"
          @click="removeFilter(key)"
          v-if="!isLoading"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, defineProps } from "vue";
import { BrushCleaning, Search, CircleX } from "lucide-vue";
import { useFiltersActuationsReceived, useStoreCompat } from "@/composables/index.js";

const props = defineProps({
  isLoading: { type: Boolean, default: false },
});

const selectedField = ref(null);
const selectedValue = ref(null);
const filtros = ref({
  FechaCreacionDesde: null,
  FechaCreacionHasta: null,
});
const activeFilters = ref({});

const hoy = new Date();

const emit = defineEmits(["buscar"]);

const { filtersFields, filterDropdown } = useFiltersActuationsReceived();

const store = useStoreCompat();

const viewData = computed(() => store.state.sumarioViews);

const selectedFieldObject = computed(() => {
  return filtersFields.value.find((f) => f.field === selectedField.value?.field);
});

const inputType = computed(() => {
  if (selectedField.value?.type) return selectedField.value.type;
  if (filterDropdown.value[selectedField.value?.field]) return "dropdown";
  return "text";
});

const getDropdownOptions = (fieldName) => {
  return filterDropdown.value[fieldName];
};

const filterDropdownOptions = computed(() => {
  let options = [
    { name: "Expedientes", value: "Expediente" },
    { name: "Sumarios", value: "Sumario" },
    { name: "UFI", value: "UNIDAD" },
    { name: "Actuaciones Preliminares - TODAS", value: "Actuacion Preliminar" },
    { name: "UFI Delitos Especiales - TODOS", value: "Unidades Fiscales de Investigacion D.E" },
  ];
  for (const actuationName in viewData.value) {
    const filterName = Object.keys(viewData.value[actuationName]?.filters || {})[0];
    options.push({
      name: filterName,
      value: viewData.value[actuationName]?.filters?.[filterName]?.actuacionesFilters?.value,
    });
  }
  options = options.filter((obj) => !Object.values(obj).some((value) => value === undefined));
  return options;
});

const handleFilter = () => {
  emit("filtrarActuaciones", buildSearchObject());
};

const getLabelFromValue = (field, value) => {
  const dropdownConfig = filterDropdown.value[field];
  if (!dropdownConfig || !dropdownConfig.options) return value;

  const match = dropdownConfig.options.find((opt) => {
    const optValue = opt[dropdownConfig.optionValue || "value"];
    return optValue === value;
  });

  return match ? match[dropdownConfig.optionLabel || "name"] : value;
};

const formatDisplayValue = (value, field) => {
  if (!value) return "";
  if (field.includes("createdAt") || typeof value === "object") {
    return formatDate(value);
  }
  return getLabelFromValue(field, value);
};

const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const cleanFilters = () => {
  selectedField.value = null;
  selectedValue.value = null;
  activeFilters.value = {};
  filtros.value.FechaCreacionDesde = null;
  filtros.value.FechaCreacionHasta = null;
  emit("filtrarActuaciones", buildSearchObject());
};

const removeFilter = (key) => {
  if (props.isLoading) return;
  const copy = { ...activeFilters.value };
  delete copy[key];
  activeFilters.value = copy;
  emit("filtrarActuaciones", buildSearchObject());
};

const updateSearchFilter = () => {
  if (!selectedField.value) return;

  const key = selectedField.value.field;

  const keysActuales = Object.keys(activeFilters.value);

  if (inputType.value === "date") {
    if (filtros.value.FechaCreacionDesde) {
      activeFilters.value["createdAtFrom"] = {
        label: "Fecha Desde",
        field: "createdAt",
        operator: ">=",
        value: filtros.value.FechaCreacionDesde,
      };
    }
    if (filtros.value.FechaCreacionHasta) {
      activeFilters.value["createdAtTo"] = {
        label: "Fecha Hasta",
        field: "createdAt",
        operator: "<=",
        value: filtros.value.FechaCreacionHasta,
      };
    }
  } else {
    activeFilters.value[key] = {
      label: selectedFieldObject.value?.name || key,
      field: key,
      operator: "=",
      value: selectedValue.value,
      visible: true,
    };

    if (selectedFieldObject.value?.addFilter) {
      Object.entries(selectedFieldObject.value.addFilter).forEach(([k, v]) => {
        activeFilters.value[k] = {
          label: k,
          field: k,
          operator: v[0]?.operator || "=",
          value: v[0]?.value || "",
          visible: false,
        };
      });
    }
  }
};

defineExpose({
  filtersFields,
  selectedField,
  selectedValue,
  inputType,
  updateSearchFilter,
});

const buildSearchObject = () => {
  const search = {};
  for (const key in activeFilters.value) {
    const filtro = activeFilters.value[key];
    if (!search[filtro.field]) {
      search[filtro.field] = [];
    }
    search[filtro.field].push({ operator: filtro.operator, value: filtro.value });
  }
  return search;
};

watch(selectedValue, () => {
  if (inputType.value !== "date") {
    updateSearchFilter();
  }
});

watch(
  () => filtros.value.FechaCreacionDesde,
  () => {
    if (inputType.value === "date") updateSearchFilter();
  }
);

watch(
  () => filtros.value.FechaCreacionHasta,
  () => {
    if (inputType.value === "date") updateSearchFilter();
  }
);

watch(
  selectedField,
  async (newVal) => {
    if (!newVal || !newVal.field) return;
    const dropdown = filterDropdown.value[newVal.field];
    selectedValue.value = null;
    if (dropdown?.loadDropdown) {
      await dropdown.loadDropdown();
    }
  },
  { immediate: true, deep: true }
);

onMounted(() => {
  if (filterDropdown.value["exp_pre_TipoActuacion.TipoActuacionID"]?.options.length === 0) {
    filterDropdown.value["exp_pre_TipoActuacion.TipoActuacionID"].options =
      filterDropdownOptions.value;
  }
});
</script>

<style>
.custom-action-button {
  display: flex;
  width: 124px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background-color: var(--Primary, #0b5195);
  color: white;
  border: none;
}

.custom-action-button:hover {
  opacity: 0.9;
  cursor: pointer;
}

.cancelar-button {
  display: flex;
  width: 124px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background: var(--White-White, #fff);
  color: #0b5195;
  border: none;
  box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.4);
}

.cancelar-button:hover {
  background: #f4f4f4 !important;
  color: #0b5195 !important;
  opacity: 0.9;
  cursor: pointer;
}

.blue-line {
  height: 4px;
  background-color: #0b5195;
  border-radius: 9999px;
}

.btn {
  display: flex;
  width: 124px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  box-shadow: 0px 1px 3px rgba(166, 175, 195, 0.4);
  transition: 0.2s ease all;
  cursor: pointer;
  font-weight: 500;
}

.btn:hover {
  opacity: 0.9;
}

.btn-primary {
  background-color: #0b5195;
  color: white;
  border: 1px solid #0b5195;
}

.btn-primary:hover {
  background-color: #0b5195;
  color: #f4f4f4;
}

.btn-outline-primary {
  background-color: #fff;
  color: #0b5195;
  border: 1px solid #0b5195;
}

.btn-outline-primary:hover {
  background-color: #f4f4f4;
}

>>> .p-calendar .p-inputtext {
  border-radius: 6px !important;
}
</style>
